import { type FC } from "react";
import { Stack } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { FilterProvider } from "@/partner/shared/components/Filter";
import { useCreateStreamMutation } from "@/partner/entities/streams";
import { STREAM_TABS, type StreamTab } from "../model";
import { StreamsFilter } from "./StreamsFilter";
import { StreamsList } from "./StreamsList";
import { EditStreamDialog } from "./EditStreamDialog";

export const Streams: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  // Неизвестное/пустое значение tab при открытом id трактуем как edit
  // (спека §1) — включая старые ссылки с tab=create.
  const tab: StreamTab = STREAM_TABS.includes(rawTab as StreamTab)
    ? (rawTab as StreamTab)
    : "edit";
  const editId = searchParams.get("id");

  const handleEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  // Как в референсном Streams/Filter.js: кнопка «Создать стрим» сразу
  // создаёт контент с дефолтами (title + запись и автопубликация включены)
  // и открывает полную форму — без промежуточного мини-диалога.
  const [createStream, { loading: creating }] = useCreateStreamMutation({
    refetchQueries: ["StreamsList"],
    onCompleted: (data) => handleEdit(data.createStream.id),
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const handleCreate = () => {
    // StreamsFilter.tsx сейчас под чужими незакоммиченными правками — проп
    // `creating` туда не прокидываем, вместо этого защита от даблклика здесь.
    if (creating) return;

    void createStream({
      variables: {
        input: {
          title: t("streams.create.defaultTitle"),
          enableRecord: true,
          autoPublishRecord: true,
        },
      },
    });
  };

  const handleClose = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  return (
    <FilterProvider>
      <Stack spacing={1}>
        <StreamsFilter onCreateStream={handleCreate} />
        <StreamsList onEditStream={handleEdit} />
      </Stack>

      <EditStreamDialog
        open={Boolean(editId)}
        contentId={editId}
        tab={tab}
        onClose={handleClose}
      />
    </FilterProvider>
  );
};
